import { useState } from 'react';
import { useIsMobile } from '@/shared/lib';
import type { DataTableColumn, DataTableRow, DataTableStatus } from '@/shared/ui/data-table/types';
import { dataTableStyles } from '@/shared/ui/data-table/styles';
import { CardSheet, renderCell } from './card-sheet';

/**
 * The card presentation of the same server page.
 *
 * A card is a list item, not a table row: a `<ul>` of `<li>` announces count
 * and position, which is what a table would have conveyed.
 *
 * WHICH COLUMN BECOMES THE HEADING
 * --------------------------------
 * A column marked `primary` is the identifying one, and it becomes the card's
 * heading. Falling back to the first column is only a guess: on a page whose
 * first column is an action ("Signature") the card would be titled with a bare
 * value and the record it describes would be nowhere on the card. A page that
 * knows which column identifies a row says so; a page that does not keeps the
 * old first-column behaviour.
 *
 * The heading column is removed from the field rows, so it is never printed
 * twice — once as the title and again as a labelled row.
 */
export function CardGrid<TData extends DataTableRow>({
  columns,
  data,
  rowKey,
  status,
  emptyTitle = 'No results',
  emptyDescription,
}: {
  columns: DataTableColumn<TData>[];
  data: TData[];
  rowKey: (row: TData) => string;
  status: DataTableStatus;
  emptyTitle?: string;
  emptyDescription?: string;
}) {
  const primaryIndex = columns.findIndex((column) => column.primary === true);
  const headingIndex = primaryIndex === -1 ? 0 : primaryIndex;
  const heading = columns[headingIndex];
  const rest = columns.filter((_, index) => index !== headingIndex);

  if (status === 'loading') return <CardGridSkeleton />;

  if (data.length === 0) {
    return (
      <div className={dataTableStyles.stateCell} role="status">
        <p className={dataTableStyles.stateTitle}>{emptyTitle}</p>
        {emptyDescription ? (
          <p className={dataTableStyles.stateDescription}>{emptyDescription}</p>
        ) : null}
      </div>
    );
  }

  return (
    <ul className={dataTableStyles.gridBare}>
      {data.map((row) => (
        <CardItem key={rowKey(row)} heading={heading} columns={rest} row={row} />
      ))}
    </ul>
  );
}

/** The placeholder cards shown while the first page is in flight. */
function CardGridSkeleton() {
  return (
    <div className={dataTableStyles.gridBare} role="status" aria-label="Loading results">
      {Array.from({ length: 3 }, (_, index) => (
        <div key={`card-${index}`} className={dataTableStyles.card}>
          <div className={dataTableStyles.skeletonCell} />
        </div>
      ))}
    </div>
  );
}

/**
 * One card.
 *
 * A column with no header is not a field — it is the actions column, which
 * carries buttons rather than a value. Rendering it as a body row produced a
 * stray `": "` with nothing before the colon, and put the buttons in the
 * middle of the card's text. It is split out and handed to the sheet instead.
 *
 * ON A CARD THE ACTIONS LIVE IN A SHEET, AT EVERY WIDTH.
 * The card carries no action buttons of its own. Tapping the card opens a
 * sheet holding the same fields plus the actions as full-width labelled rows.
 * Three 28px icon buttons in every card is a row of controls competing with
 * the data, and on a phone they are small enough to be a mis-tap risk; a touch
 * screen also cannot show the hover tooltip that carries an icon button's
 * label, so the sheet spells each action out.
 *
 * THE TAP TARGET IS A STRETCHED OVERLAY, NOT A WRAPPING BUTTON.
 * A cell may render its own control — the requirements card's Execution cell
 * is a link to the execution. Wrapping the whole card in a `<button>` put that
 * link INSIDE a button, which is invalid HTML and a real accessibility
 * failure: a button may not contain interactive content, and the browser
 * cannot say which control a tap meant. So the card is a positioned `<div>`
 * and the tap target is one absolutely-positioned `<button>` stretched over
 * it, sitting UNDER the content. A cell that renders a control raises itself
 * above the overlay (`relative z-10`), so it stays clickable and the rest of
 * the card still opens the sheet. The overlay is a real button, so it is
 * focusable, announced, and activated by Enter and Space for free.
 *
 * The sheet is anchored to the bottom edge on a phone and to the right edge on
 * a pointer — a bottom sheet on a wide screen is a strip pinned to the bottom
 * of a tall window with the record it describes far above it.
 */
function CardItem<TData extends DataTableRow>({
  heading,
  columns,
  row,
}: {
  heading: DataTableColumn<TData> | undefined;
  columns: DataTableColumn<TData>[];
  row: TData;
}) {
  const [sheetOpen, setSheetOpen] = useState(false);
  const isMobile = useIsMobile();
  const fields = columns.filter((column) => column.header !== '');
  const actions = columns.filter((column) => column.header === '');

  return (
    <li className={dataTableStyles.card}>
      {/*
       * The content is rendered ONCE. There is no second, CSS-hidden copy: a
       * hidden duplicate still doubles the DOM, and any cell with a side
       * effect would fire twice.
       */}
      <CardContent heading={heading} fields={fields} row={row} />

      {/*
       * The tap target. It is stretched over the card and sits UNDER the
       * content, so a cell's own control (raised above it) wins the tap and
       * everything else falls through to the sheet.
       */}
      <button
        type="button"
        className={dataTableStyles.cardOverlay}
        aria-label={heading ? `Open ${heading.header.toLowerCase()} details` : 'Open details'}
        onClick={() => setSheetOpen(true)}
      />

      <CardSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        side={isMobile ? 'bottom' : 'right'}
        heading={heading}
        columns={fields}
        actions={actions}
        row={row}
      />
    </li>
  );
}

/** The heading and the field rows — the card's own content, in either wrapper. */
function CardContent<TData extends DataTableRow>({
  heading,
  fields,
  row,
}: {
  heading: DataTableColumn<TData> | undefined;
  fields: DataTableColumn<TData>[];
  row: TData;
}) {
  return (
    /*
     * `relative z-10` lifts the content above the card's overlay button, so
     * the text is not covered by it. A cell that renders its own control is
     * raised again inside `cardRow` so it wins the tap.
     */
    <div className={dataTableStyles.cardContent}>
      {/*
       * The heading is a `<div>`, not a `<p>`: a cell renderer returns whatever
       * the record needs — a name over a code, a badge, a link — and a `<p>`
       * may only contain phrasing content. A cell that returned a `<div>` (the
       * products page's name-over-code) was invalid HTML inside a paragraph,
       * and React said so.
       */}
      {heading ? <div className={dataTableStyles.cardHeader}>{renderCell(heading, row)}</div> : null}
      <div className={dataTableStyles.cardBody}>
        {fields.map((column) => (
          <div key={column.id} className={dataTableStyles.cardRow}>
            <span className={dataTableStyles.cardRowLabel}>{column.header}:</span>
            <span className={dataTableStyles.cardInteractive}>
              {renderCell(column, row, 'card')}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}